import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check } from '../../icons'

import { cn } from '../../lib/utils'

const toggleChipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer select-none whitespace-nowrap',
  {
    variants: {
      variant: {
        default: '',
        platform: '',
        outline: '',
      },
      tone: {
        light: '',
        dark: '',
      },
      size: {
        default: 'px-3.5 py-1.5 text-sm',
        sm: 'px-3 py-1 text-xs',
      },
      selected: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // default variant
      {
        variant: 'default',
        selected: false,
        className: 'border-border bg-background text-foreground hover:bg-muted/60',
      },
      {
        variant: 'default',
        selected: true,
        className: 'border-primary bg-primary text-primary-foreground',
      },
      // platform variant — light
      {
        variant: 'platform',
        selected: false,
        tone: 'light',
        className: 'border-border bg-background text-foreground hover:bg-muted/60',
      },
      {
        variant: 'platform',
        selected: true,
        tone: 'light',
        className: 'border-primary/40 bg-primary/10 text-primary',
      },
      // platform variant — dark
      {
        variant: 'platform',
        selected: false,
        tone: 'dark',
        className: 'border-border bg-foreground/10 text-foreground hover:bg-foreground/15',
      },
      {
        variant: 'platform',
        selected: true,
        tone: 'dark',
        className: 'border-primary/40 bg-primary/10 text-primary',
      },
      // outline variant
      {
        variant: 'outline',
        selected: false,
        className: 'border-border bg-transparent text-foreground hover:bg-muted/40',
      },
      {
        variant: 'outline',
        selected: true,
        className: 'border-primary bg-primary/10 text-primary',
      },
    ],
    defaultVariants: {
      variant: 'default',
      tone: 'light',
      size: 'default',
      selected: false,
    },
  },
)

export interface ToggleChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    Omit<VariantProps<typeof toggleChipVariants>, 'selected'> {
  selected?: boolean
  showCheck?: boolean
  /** 'icon' renders a plain check icon; 'badge' renders a filled circle with the check inside (original homepage style). */
  checkStyle?: 'icon' | 'badge'
}

const ToggleChip = React.forwardRef<HTMLButtonElement, ToggleChipProps>(
  (
    {
      className,
      variant,
      tone,
      size,
      selected = false,
      showCheck = false,
      checkStyle = 'icon',
      disabled,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const checkEl =
      showCheck && selected ? (
        checkStyle === 'badge' ? (
          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[hsl(var(--platform-card))] text-[hsl(var(--platform-deep))]">
            <Check className="h-[9px] w-[9px]" strokeWidth={2.5} />
          </span>
        ) : (
          <Check className="h-3 w-3 shrink-0" />
        )
      ) : null

    if (disabled) {
      return (
        <span
          aria-disabled="true"
          className={cn(
            toggleChipVariants({ variant, tone, size, selected }),
            'pointer-events-none opacity-50',
            className,
          )}
          {...(props as React.HTMLAttributes<HTMLSpanElement>)}
        >
          {checkEl}
          {children}
        </span>
      )
    }

    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={selected}
        className={cn(
          toggleChipVariants({ variant, tone, size, selected }),
          className,
        )}
        onClick={onClick}
        {...props}
      >
        {checkEl}
        {children}
      </button>
    )
  },
)
ToggleChip.displayName = 'ToggleChip'

export { ToggleChip, toggleChipVariants }
